import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { debtorFullName, filingDebtors } from "../utils/debtors";

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN = 72;
const CONTENT_WIDTH = PAGE_WIDTH - (MARGIN * 2);
const BLACK = rgb(0, 0, 0);
const BODY_SIZE = 11;
const DOUBLE_SPACE_LINE_HEIGHT = BODY_SIZE * 2;

function safeLine(...parts) {
  return parts
    .filter((part) => part !== null && part !== undefined && String(part).trim())
    .map((part) => String(part).trim())
    .join(" ");
}

function parseLocalDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  }

  const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatLongDate(value) {
  const date = parseLocalDate(value);
  if (!date) return "";
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function calculateExtensionDate(deadline, daysExtended) {
  const date = parseLocalDate(deadline);
  const days = Number(daysExtended);
  if (!date || !Number.isInteger(days) || days < 1) return null;

  date.setDate(date.getDate() + days);
  if (date.getDay() === 6) date.setDate(date.getDate() + 2);
  if (date.getDay() === 0) date.setDate(date.getDate() + 1);
  return date;
}

function filingDebtorNames(caseData) {
  return filingDebtors(caseData?.debtors)
    .map(debtorFullName)
    .filter(Boolean)
    .slice(0, 2);
}

function drawCentered(page, text, font, size, y) {
  const width = font.widthOfTextAtSize(text, size);
  page.drawText(text, {
    x: (PAGE_WIDTH - width) / 2,
    y,
    size,
    font,
    color: BLACK,
  });
}

function drawFittedText(page, text, { x, y, maxWidth, font, size = 11, minSize = 8 }) {
  let fittedSize = size;
  while (fittedSize > minSize && font.widthOfTextAtSize(text, fittedSize) > maxWidth) {
    fittedSize -= 0.25;
  }
  page.drawText(text, { x, y, size: fittedSize, font, color: BLACK });
}

function drawWrappedText(page, text, {
  x = MARGIN,
  y,
  width = CONTENT_WIDTH,
  font,
  size = 11,
  lineHeight = 15,
  firstLineIndent = 0,
}) {
  const firstLineWidth = Math.max(1, width - firstLineIndent);
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return y;

  const lines = [];
  let line = "";
  let lineWidth = firstLineWidth;

  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (font.widthOfTextAtSize(candidate, size) <= lineWidth || !line) {
      line = candidate;
      return;
    }
    lines.push(line);
    line = word;
    lineWidth = width;
  });
  if (line) lines.push(line);

  lines.forEach((lineText, index) => {
    page.drawText(lineText, {
      x: x + (index === 0 ? firstLineIndent : 0),
      y: y - (index * lineHeight),
      size,
      font,
      color: BLACK,
    });
  });

  return y - (lines.length * lineHeight);
}

function drawNumberedParagraph(page, number, text, y, font, lineHeight = DOUBLE_SPACE_LINE_HEIGHT) {
  page.drawText(`${number}.`, {
    x: MARGIN + 18,
    y,
    size: BODY_SIZE,
    font,
    color: BLACK,
  });
  return drawWrappedText(page, text, {
    x: MARGIN + 42,
    y,
    width: CONTENT_WIDTH - 42,
    font,
    size: BODY_SIZE,
    lineHeight,
  });
}

function drawCourtHeading(page, bold, topY) {
  drawCentered(page, "United States Bankruptcy Court", bold, 15, topY);
  drawCentered(page, "Eastern District of Tennessee", bold, 15, topY - 19);
  drawCentered(page, "Northeastern Division", bold, 15, topY - 38);
}

function drawCaption(page, { names, caseNumber, chapter, regular, bold, topY }) {
  const bottomY = topY - 70;
  const dividerX = 306;
  page.drawLine({
    start: { x: dividerX, y: bottomY },
    end: { x: dividerX, y: topY },
    thickness: 1,
    color: BLACK,
  });

  page.drawText("In re", { x: MARGIN, y: topY - 14, size: 11, font: regular, color: BLACK });
  drawFittedText(page, names[0] || "Debtor", {
    x: 150,
    y: topY - 14,
    maxWidth: 145,
    font: bold,
  });
  if (names[1]) {
    drawFittedText(page, names[1], {
      x: 150,
      y: topY - 31,
      maxWidth: 145,
      font: bold,
    });
  }
  page.drawText("Debtor(s)", { x: 190, y: bottomY + 4, size: 10, font: regular, color: BLACK });

  page.drawText("Case No.", { x: 330, y: topY - 14, size: 11, font: bold, color: BLACK });
  drawFittedText(page, String(caseNumber || ""), {
    x: 398,
    y: topY - 14,
    maxWidth: 142,
    font: regular,
  });
  page.drawText("Chapter", { x: 330, y: topY - 48, size: 11, font: bold, color: BLACK });
  page.drawText(String(chapter || "7"), { x: 398, y: topY - 48, size: 11, font: regular, color: BLACK });
}

function drawSignatureBlock(page, { x, y, regular, bold, slash = "s/" }) {
  page.drawText(`${slash} Oliver Cisowski`, { x, y, size: 10.5, font: bold, color: BLACK });
  page.drawLine({
    start: { x, y: y - 3 },
    end: { x: x + 165, y: y - 3 },
    thickness: 0.7,
    color: BLACK,
  });

  const lines = [
    "Signature of Attorney",
    "Oliver Cisowski 036759",
    "Cisowski Law",
    "3119 Bristol Hwy Ste 217",
    "Johnson City, TN 37601",
    "(423) 342-4722",
    "oliver@cisowskilaw.com",
  ];
  lines.forEach((line, index) => {
    page.drawText(line, {
      x,
      y: y - 17 - (index * 13),
      size: 10.5,
      font: regular,
      color: BLACK,
    });
  });
}

function setDocumentMetadata(pdfDoc, title) {
  pdfDoc.setTitle(title);
  pdfDoc.setAuthor("Cisowski Law");
  pdfDoc.setCreator("Bankruptcy Manager");
  pdfDoc.setProducer("Bankruptcy Manager");
}

function resolvedFields(caseData, daysExtended, today) {
  const deadline = parseLocalDate(caseData?.reaffirmation_deadline);
  const extensionDate = calculateExtensionDate(deadline, daysExtended);
  if (!deadline) throw new Error("This case does not have a reaffirmation deadline.");
  if (!extensionDate) throw new Error("Enter a positive whole number of days.");

  return {
    names: filingDebtorNames(caseData),
    caseNumber: caseData?.case_number || "",
    chapter: caseData?.chapter || "7",
    deadline: formatLongDate(deadline),
    daysExtended: Number(daysExtended),
    extensionDate: formatLongDate(extensionDate),
    today: formatLongDate(today),
  };
}

export async function buildReaffirmationExtensionMotionPdf({
  caseData,
  daysExtended,
  today = new Date(),
}) {
  const fields = resolvedFields(caseData, daysExtended, today);
  const pdfDoc = await PDFDocument.create();
  const regular = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  setDocumentMetadata(pdfDoc, "Motion to Extend Deadline to File Reaffirmation Agreements");

  const pageOne = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  drawCourtHeading(pageOne, bold, 733);
  drawCaption(pageOne, { ...fields, regular, bold, topY: 668 });
  drawCentered(
    pageOne,
    "Debtors' Motion to Extend Deadline to File Reaffirmation Agreements",
    bold,
    13,
    565,
  );

  let y = drawWrappedText(
    pageOne,
    "Debtor(s), by counsel, moves this Honorable Court to extend the deadline to file reaffirmation agreements pursuant to Federal Rule of Bankruptcy Procedure 4008(a). In support of this motion, debtors would show the following:",
    {
      y: 531,
      font: regular,
      size: BODY_SIZE,
      lineHeight: DOUBLE_SPACE_LINE_HEIGHT,
      firstLineIndent: 36,
    },
  );
  y -= 16;
  y = drawNumberedParagraph(
    pageOne,
    1,
    `Debtors desire to reaffirm certain debts and the time for reaffirming is about to expire, ${fields.deadline} being the deadline. Bankruptcy Rule 4008 requires reaffirmation agreements to be filed no later than 60 days after the date first set for the meeting of creditors.`,
    y,
    regular,
  );
  y -= 16;
  y = drawNumberedParagraph(
    pageOne,
    2,
    "The filing deadline for reaffirmation agreements may be extended \"at any time\" pursuant to Rule 4008; however, 11 USC §524(c)(1) still requires reaffirmation agreements to be entered into before a debtors' discharge.",
    y,
    regular,
  );
  y -= 16;
  y = drawNumberedParagraph(
    pageOne,
    3,
    `An extension of the deadline for filing reaffirmation agreements by ${fields.daysExtended} days to ${fields.extensionDate}, together with a corresponding delay in entry of discharge order will allow the debtors to file all of their reaffirmation agreements as provided by the Bankruptcy Code and Rules.`,
    y,
    regular,
  );
  y -= 16;
  drawWrappedText(
    pageOne,
    "Wherefore the Debtors pray that an order be entered extending the deadline to file reaffirmation agreements under Bankruptcy Rule 4008, as well as a corresponding extension of the time before the Court enters a discharge order in this case.",
    {
      y,
      font: regular,
      size: BODY_SIZE,
      lineHeight: DOUBLE_SPACE_LINE_HEIGHT,
      firstLineIndent: 36,
    },
  );

  const pageTwo = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  pageTwo.drawText(`Date: ${fields.today}`, {
    x: MARGIN,
    y: 714,
    size: BODY_SIZE,
    font: regular,
    color: BLACK,
  });
  drawSignatureBlock(pageTwo, { x: 348, y: 714, regular, bold, slash: "s/" });

  drawCentered(pageTwo, "Certificate of Service", bold, 13, 560);
  drawWrappedText(
    pageTwo,
    `I hereby certify that on ${fields.today} a copy of attached Motion to Extend Deadline to File Reaffirmation Agreements and Proposed Order were served electronically to the US Trustee and Chapter 7 Trustee through the Court's electronic noticing.`,
    {
      y: 520,
      font: regular,
      size: BODY_SIZE,
      lineHeight: DOUBLE_SPACE_LINE_HEIGHT,
      firstLineIndent: 36,
    },
  );
  drawSignatureBlock(pageTwo, { x: 348, y: 390, regular, bold, slash: "/s/" });

  return pdfDoc.save();
}

export async function buildReaffirmationExtensionOrderPdf({
  caseData,
  daysExtended,
  today = new Date(),
}) {
  const fields = resolvedFields(caseData, daysExtended, today);
  const pdfDoc = await PDFDocument.create();
  const regular = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  setDocumentMetadata(pdfDoc, "Order on Motion to Extend Deadline to File Reaffirmation Agreements");

  const page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  // Four inches (288 points) of clear space precede the first court-caption text.
  drawCourtHeading(page, bold, 493);
  drawCaption(page, { ...fields, regular, bold, topY: 428 });
  drawCentered(
    page,
    "Order on Motion to Extend Deadline to File Reaffirmation Agreements",
    bold,
    13,
    325,
  );

  drawWrappedText(
    page,
    `The Debtor, through counsel, having filed the Motion to Extend Deadline to File Reaffirmation Agreements in this case on ${fields.today}, the Court, as allowed by Federal Rule of Bankruptcy Procedure 4008(a), will extend the deadline to file reaffirmation agreements to ${fields.extensionDate}.`,
    {
      y: 289,
      font: regular,
      size: BODY_SIZE,
      lineHeight: DOUBLE_SPACE_LINE_HEIGHT,
      firstLineIndent: 36,
    },
  );
  drawCentered(page, "# # #", regular, BODY_SIZE, 195);
  page.drawText("Approved for entry:", {
    x: MARGIN,
    y: 166,
    size: BODY_SIZE,
    font: regular,
    color: BLACK,
  });
  drawSignatureBlock(page, { x: 348, y: 148, regular, bold, slash: "s/" });

  return pdfDoc.save();
}

export function downloadReaffirmationExtensionPdf({ bytes, filename }) {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  requestAnimationFrame(() => {
    URL.revokeObjectURL(url);
    link.remove();
  });
}

function safeFilenamePart(value) {
  return String(value || "case").replace(/[^a-zA-Z0-9_-]+/g, "_");
}

export async function prepareReaffirmationExtensionPdfs({ caseData, daysExtended }) {
  if (!caseData) throw new Error("Case information is required.");

  const [motionBytes, orderBytes] = await Promise.all([
    buildReaffirmationExtensionMotionPdf({ caseData, daysExtended }),
    buildReaffirmationExtensionOrderPdf({ caseData, daysExtended }),
  ]);
  const identifier = safeFilenamePart(caseData.case_number || caseData.case_id);
  return {
    motion: {
      bytes: motionBytes,
      filename: `Reaffirmation_Extension_Motion_${identifier}.pdf`,
    },
    order: {
      bytes: orderBytes,
      filename: `Reaffirmation_Extension_Order_${identifier}.pdf`,
    },
  };
}
